import urljoin from 'url-join';
import { portalApi } from './index.api';
import { normalizeLoginStatusEmailKey } from '../../../utils/loginStatusFetch';
import { normalizeTalentLmsSyncEmailKey } from '../../../utils/talentLmsSyncFetch';

export const adminApi = portalApi.injectEndpoints({
	endpoints: (builder) => ({
		// Get all users (paginated)
		users: builder.query({
			query: ({ skip = 0, take = 100 } = {}) => {
				const url = urljoin("admin", "users");
				return {
					// baseQueryWithAuth handles authorization header automatically
					url: `${url}?skip=${skip}&page_size=${take}`,
					method: 'GET',
				};
			},
			providesTags: ['users'],
			serializeQueryArgs: ({ endpointName }) => {
				return endpointName;
			},
			merge: (currentCache, newItems) => {
				// Remove duplicates by email before merging
				const existingEmails = new Set((currentCache || []).map(u => u.email));
				const uniqueNewItems = (newItems || []).filter(u => !existingEmails.has(u.email));
				return [...(currentCache || []), ...uniqueNewItems];
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				return currentArg?.skip !== previousArg?.skip;
			},
		}),
		// Search users (paginated)
		searchUsers: builder.query({
			query: ({ searchText, skip = 0, take = 100 }) => {
				const url = urljoin("admin", "users", "search", searchText);
				return {
					// baseQueryWithAuth handles authorization header automatically
					url: `${url}?skip=${skip}&page_size=${take}`,
					method: 'GET',
				};
			},
			providesTags: (result) => result ? result.map((user) => ({ type: 'user', id: user.email })) : ['user'],
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.searchText}`;
			},
			merge: (currentCache, newItems) => {
				// Remove duplicates by email before merging
				const existingEmails = new Set((currentCache || []).map(u => u.email));
				const uniqueNewItems = (newItems || []).filter(u => !existingEmails.has(u.email));
				return [...(currentCache || []), ...uniqueNewItems];
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				return currentArg?.skip !== previousArg?.skip || currentArg?.searchText !== previousArg?.searchText;
			},
		}),
		// Get all roles (paginated)
		roles: builder.query({
			query: ({ skip = 0, take = 100 } = {}) => {
				const url = urljoin("admin", "users", "roles");
				return {
					// baseQueryWithAuth handles authorization header automatically
					url: `${url}?skip=${skip}&page_size=${take}`,
					method: 'GET',
				};
			},
			providesTags: ['roles'],
			serializeQueryArgs: ({ endpointName }) => {
				return endpointName;
			},
			merge: (currentCache, newItems) => {
				return [...(currentCache || []), ...(newItems || [])];
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				return currentArg?.skip !== previousArg?.skip;
			},
		}),
		// Get all roles (no pagination)
		allRoles: builder.query({
			query: () => {
				const url = urljoin("admin", "users", "roles", "all");
				return {
					// baseQueryWithAuth handles authorization header automatically
					url,
					method: 'GET',
				};
			},
			providesTags: ['roles'],
			serializeQueryArgs: ({ endpointName }) => endpointName,
		}),
		// Search roles (paginated)
		searchRoles: builder.query({
			query: ({ searchText, skip = 0, take = 100 }) => {
				const url = urljoin("admin", "users", "roles", "search", searchText);
				return {
					// baseQueryWithAuth handles authorization header automatically
					url: `${url}?skip=${skip}&page_size=${take}`,
					method: 'GET',
				};
			},
			providesTags: (result) => result ? result.map((role) => ({ type: 'role', id: role.name })) : ['role'],
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.searchText}`;
			},
			merge: (currentCache, newItems) => {
				return [...(currentCache || []), ...(newItems || [])];
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				return currentArg?.skip !== previousArg?.skip || currentArg?.searchText !== previousArg?.searchText;
			},
		}),
		// Get all managed projects (paginated)
		managedProjects: builder.query({
			query: ({ skip = 0, take = 100 } = {}) => {
				const url = urljoin("admin", "users", "projects");
				return {
					// baseQueryWithAuth handles authorization header automatically
					url: `${url}?skip=${skip}&page_size=${take}`,
					method: 'GET',
				};
			},
			providesTags: ['projects'],
			serializeQueryArgs: ({ endpointName }) => {
				return endpointName;
			},
			merge: (currentCache, newItems) => {
				return [...(currentCache || []), ...(newItems || [])];
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				return currentArg?.skip !== previousArg?.skip;
			},
		}),
		// Search projects (paginated)
		searchProjects: builder.query({
			query: ({ searchText, skip = 0, take = 100 }) => {
				const url = urljoin("admin", "users", "projects", "search", searchText);
				return {
					// baseQueryWithAuth handles authorization header automatically
					url: `${url}?skip=${skip}&page_size=${take}`,
					method: 'GET',
				};
			},
			providesTags: (result) => result ? result.map((project) => ({ type: 'project', id: project.projectId })) : ['project'],
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.searchText}`;
			},
			merge: (currentCache, newItems) => {
				return [...(currentCache || []), ...(newItems || [])];
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				return currentArg?.skip !== previousArg?.skip || currentArg?.searchText !== previousArg?.searchText;
			},
		}),
		// Get projects for a user (scopes)
		userProjects: builder.query({
			query: ({ email, skip = 0, take = 100 }) => {
				const url = urljoin("admin", "users", encodeURIComponent(email), "projects");
				return {
					// baseQueryWithAuth handles authorization header automatically
					url: `${url}?skip=${skip}&page_size=${take}`,
					method: 'GET',
				};
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.email}`;
			},
			merge: (currentCache, newItems) => {
				return [...(currentCache || []), ...(newItems || [])];
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				return currentArg?.skip !== previousArg?.skip || currentArg?.email !== previousArg?.email;
			},
		}),
		// Get roles for a user (scopes)
		userRoles: builder.query({
			query: ({ email, skip = 0, take = 100 }) => {
				const url = urljoin("admin", "users", encodeURIComponent(email), "roles");
				return {
					// baseQueryWithAuth handles authorization header automatically
					url: `${url}?skip=${skip}&page_size=${take}`,
					method: 'GET',
				};
			},
			transformResponse: (response) => Array.isArray(response)
				? response.map((role) => ({
					...role,
					id: role.name || role.Name,
					name: role.name || role.Name,
					description: role.description || role.Description
				}))
				: [],
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.email}`;
			},
			merge: (currentCache, newItems) => {
				return [...(currentCache || []), ...(newItems || [])];
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				return currentArg?.skip !== previousArg?.skip || currentArg?.email !== previousArg?.email;
			},
		}),
		userLoginStatus: builder.query({
			query: ({ email }) => {
				const normalizedEmail = normalizeLoginStatusEmailKey(email);
				return {
					url: urljoin('admin', 'users', 'salesforce', encodeURIComponent(normalizedEmail)),
					method: 'GET',
				};
			},
			transformResponse: (response) => ({
				status: response?.status ?? response?.Status ?? '',
			}),
			serializeQueryArgs: ({ endpointName, queryArgs }) =>
				`${endpointName}-${normalizeLoginStatusEmailKey(queryArgs.email)}`,
			keepUnusedDataFor: 0,
		}),
		userTalentLmsSync: builder.query({
			query: ({ email }) => {
				const normalizedEmail = normalizeTalentLmsSyncEmailKey(email);
				return {
					url: urljoin('admin', 'users', 'talent-lms', encodeURIComponent(normalizedEmail)),
					method: 'GET',
				};
			},
			transformResponse: (response) => ({
				syncStatus: response?.syncStatus ?? response?.SyncStatus ?? 'NeverSynced',
				talentLmsUserId: response?.talentLmsUserId ?? response?.TalentLmsUserId ?? '',
				lastSyncAttemptUtc: response?.lastSyncAttemptUtc ?? response?.LastSyncAttemptUtc ?? '',
				lastSyncSuccessUtc: response?.lastSyncSuccessUtc ?? response?.LastSyncSuccessUtc ?? '',
				lastSyncError: response?.lastSyncError ?? response?.LastSyncError ?? '',
				authorizedBranchIds: response?.authorizedBranchIds ?? response?.AuthorizedBranchIds ?? [],
				branches: response?.branches ?? response?.Branches ?? [],
			}),
			serializeQueryArgs: ({ endpointName, queryArgs }) =>
				`${endpointName}-${normalizeTalentLmsSyncEmailKey(queryArgs.email)}`,
			keepUnusedDataFor: 0,
		}),
		reconcileUserTalentLms: builder.mutation({
			query: ({ email }) => ({
				url: urljoin('admin', 'users', 'talent-lms', encodeURIComponent(normalizeTalentLmsSyncEmailKey(email)), 'reconcile'),
				method: 'POST',
			}),
			transformResponse: (response) => ({
				syncStatus: response?.syncStatus ?? response?.SyncStatus ?? '',
				warnings: response?.warnings ?? response?.Warnings ?? [],
			}),
		}),
		talentLmsSyncQueue: builder.query({
			query: () => ({
				url: urljoin('admin', 'users', 'talent-lms', 'queue'),
				method: 'GET',
			}),
			transformResponse: (response) => ({
				enabled: response?.enabled ?? response?.Enabled ?? false,
				pendingCount: response?.pendingCount ?? response?.PendingCount ?? 0,
			}),
		}),
		talentLmsSyncFailures: builder.query({
			query: () => ({
				url: urljoin('admin', 'users', 'talent-lms', 'failures'),
				method: 'GET',
			}),
			transformResponse: (response) => {
				const failures = response?.failures ?? response?.Failures ?? [];
				return failures.map((failure) => ({
					email: failure?.email ?? failure?.Email ?? '',
					message: failure?.message ?? failure?.Message ?? '',
				}));
			},
		}),
		runTalentLmsSync: builder.mutation({
			query: () => ({
				url: urljoin('admin', 'users', 'talent-lms', 'sync'),
				method: 'POST',
			}),
			transformResponse: (response) => {
				const failures = response?.failures ?? response?.Failures ?? [];
				return {
					pendingCountBefore: response?.pendingCountBefore ?? response?.PendingCountBefore ?? 0,
					reconciledCount: response?.reconciledCount ?? response?.ReconciledCount ?? 0,
					pendingCountAfter: response?.pendingCountAfter ?? response?.PendingCountAfter ?? 0,
					newlyQueuedCount: response?.newlyQueuedCount ?? response?.NewlyQueuedCount ?? 0,
					failures: failures.map((failure) => ({
						email: failure?.email ?? failure?.Email ?? '',
						message: failure?.message ?? failure?.Message ?? '',
					})),
				};
			},
			invalidatesTags: ['users'],
		}),
		userRegistrationEligibility: builder.query({
			query: ({ email }) => {
				const normalizedEmail = normalizeLoginStatusEmailKey(email);
				return {
					url: urljoin('admin', 'users', 'registration-eligibility', encodeURIComponent(normalizedEmail)),
					method: 'GET',
				};
			},
			transformResponse: (response) => ({
				allowed: response?.allowed ?? response?.Allowed ?? false,
				message: response?.message ?? response?.Message ?? '',
			}),
			serializeQueryArgs: ({ endpointName, queryArgs }) =>
				`${endpointName}-${normalizeLoginStatusEmailKey(queryArgs.email)}`,
			keepUnusedDataFor: 0,
		}),
		rolePermissions: builder.query({
			query: ({ roleName }) => ({
				url: urljoin('admin', 'users', 'roles', encodeURIComponent(roleName), 'permissions'),
				method: 'GET',
			}),
			transformResponse: (response) => {
				const permissions = response?.permissions ?? response?.Permissions ?? [];
				return Array.isArray(permissions) ? permissions : [];
			},
			serializeQueryArgs: ({ endpointName, queryArgs }) =>
				`${endpointName}-${queryArgs.roleName}`,
			providesTags: (result, error, { roleName }) => [
				{ type: 'roles', id: `permissions-${roleName}` },
			],
		}),
		removeRolePermission: builder.mutation({
			query: ({ roleName, permission }) => ({
				url: urljoin('admin', 'users', 'roles', encodeURIComponent(roleName), 'permissions', 'remove'),
				method: 'POST',
				body: { permission },
			}),
			invalidatesTags: (result, error, { roleName }) => [
				{ type: 'roles', id: `permissions-${roleName}` },
				'roles',
			],
		}),
		addRolePermission: builder.mutation({
			query: ({ roleName, permission }) => ({
				url: urljoin('admin', 'users', 'roles', encodeURIComponent(roleName), 'permissions', 'add'),
				method: 'POST',
				body: { permission },
			}),
			invalidatesTags: (result, error, { roleName }) => [
				{ type: 'roles', id: `permissions-${roleName}` },
				'roles',
			],
		}),
		permissionsCatalog: builder.query({
			query: () => ({
				url: urljoin('admin', 'users', 'roles', 'permissions', 'catalog'),
				method: 'GET',
			}),
			transformResponse: (response) => {
				const permissions = response?.permissions ?? response?.Permissions ?? [];
				return Array.isArray(permissions) ? permissions : [];
			},
			providesTags: ['roles'],
		}),
		defaultRoles: builder.query({
			query: () => ({
				url: urljoin('admin', 'users', 'roles', 'definitions', 'default'),
				method: 'GET',
			}),
			transformResponse: (response) => {
				const roleNames = response?.roleNames ?? response?.RoleNames ?? [];
				return Array.isArray(roleNames) ? roleNames : [];
			},
			providesTags: ['roles'],
		}),
		resetRolePermissions: builder.mutation({
			query: ({ roleName }) => ({
				url: urljoin('admin', 'users', 'roles', encodeURIComponent(roleName), 'permissions', 'reset'),
				method: 'POST',
			}),
			transformResponse: (response) => {
				const permissions = response?.permissions ?? response?.Permissions ?? [];
				return Array.isArray(permissions) ? permissions : [];
			},
			invalidatesTags: (result, error, { roleName }) => [
				{ type: 'roles', id: `permissions-${roleName}` },
				'roles',
			],
		}),
		resetAllRoles: builder.mutation({
			query: () => ({
				url: urljoin('admin', 'users', 'roles', 'reset'),
				method: 'POST',
			}),
			transformResponse: (response) => ({
				rolesCreated: response?.rolesCreated ?? response?.RolesCreated ?? 0,
				rolesUpdated: response?.rolesUpdated ?? response?.RolesUpdated ?? 0,
				rolesRemoved: response?.rolesRemoved ?? response?.RolesRemoved ?? 0,
				removedRoleNames: response?.removedRoleNames ?? response?.RemovedRoleNames ?? [],
			}),
			invalidatesTags: ['roles', 'users'],
		}),
		// Get users for a project (scopes)
		projectUsers: builder.query({
			query: ({ projectId, skip = 0, take = 100 }) => {
				const url = urljoin("admin", "users", "projects", projectId, "users");
				return {
					// baseQueryWithAuth handles authorization header automatically
					url: `${url}?skip=${skip}&page_size=${take}`,
					method: 'GET',
				};
			},
			transformResponse: (response) => Array.isArray(response)
				? response.map((user) => ({
					...user,
					id: user.email || user.Email,
					name: `${user.firstName || user.FirstName || ''} ${user.lastName || user.LastName || ''}`.trim() || user.email || user.Email,
					email: user.email || user.Email
				}))
				: [],
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.projectId}`;
			},
			merge: (currentCache, newItems) => {
				return [...(currentCache || []), ...(newItems || [])];
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				return currentArg?.skip !== previousArg?.skip || currentArg?.projectId !== previousArg?.projectId;
			},
		}),
		// Get users for a role (scopes)
		roleUsers: builder.query({
			query: ({ roleName, skip = 0, take = 100 }) => {
				const url = urljoin("admin", "users", "roles", encodeURIComponent(roleName), "users");
				return {
					// baseQueryWithAuth handles authorization header automatically
					url: `${url}?skip=${skip}&page_size=${take}`,
					method: 'GET',
				};
			},
			transformResponse: (response) => Array.isArray(response)
				? response.map((user) => ({
					...user,
					id: user.email || user.Email,
					name: `${user.firstName || user.FirstName || ''} ${user.lastName || user.LastName || ''}`.trim() || user.email || user.Email,
					email: user.email || user.Email
				}))
				: [],
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.roleName}`;
			},
			merge: (currentCache, newItems) => {
				return [...(currentCache || []), ...(newItems || [])];
			},
			forceRefetch: ({ currentArg, previousArg }) => {
				return currentArg?.skip !== previousArg?.skip || currentArg?.roleName !== previousArg?.roleName;
			},
		}),
		// Add user to project
		addUserToProject: builder.mutation({
			query: ({ projectId, user, comment = '' }) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "users", "project", projectId, "add"),
				body: { instance: { ...user }, comment },
				method: 'POST',
			}),
			invalidatesTags: ['projects', 'users']
		}),
		// Add users to project (collection)
		addUsersToProject: builder.mutation({
			query: ({ projectId, users, comment = '' }) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "users", "project", projectId, "add", "collection"),
				body: { instance: users, comment },
				method: 'POST',
			}),
			invalidatesTags: ['projects', 'users']
		}),
		// Add projects to user (collection)
		addProjectsToUser: builder.mutation({
			query: ({ email, projectIds, user, comment = '' }) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "users", "user", encodeURIComponent(email), "projects", "add", "collection"),
				body: { instance: { user: { ...user, email }, projectIds }, comment },
				method: 'POST',
			}),
			invalidatesTags: ['projects', 'users']
		}),
		// Add roles to user (collection)
		addRolesToUser: builder.mutation({
			query: ({ email, roleNames, user, comment = '' }) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "users", "user", encodeURIComponent(email), "roles", "add", "collection"),
				body: { instance: { user: { ...user, email }, roleNames }, comment },
				method: 'POST',
			}),
			invalidatesTags: ['roles', 'users']
		}),
		// Remove roles from user (collection)
		removeRolesFromUser: builder.mutation({
			query: ({ email, roleNames, user, comment = '' }) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "users", "user", encodeURIComponent(email), "roles", "remove", "collection"),
				body: { instance: { user: { ...user, email }, roleNames }, comment },
				method: 'POST',
			}),
			invalidatesTags: ['roles', 'users']
		}),
		// Add user to role
		addUserToRole: builder.mutation({
			query: ({ email, roleName, user, comment = '' }) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "users", "user", encodeURIComponent(email), "role", encodeURIComponent(roleName), "add"),
				body: { instance: { ...user, email }, comment },
				method: 'POST',
			}),
			invalidatesTags: ['roles', 'users']
		}),
		// Add users to role (collection)
		addUsersToRole: builder.mutation({
			query: ({ roleName, users, comment = '' }) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "users", "roles", encodeURIComponent(roleName), "users", "add", "collection"),
				body: { instance: users, comment },
				method: 'POST',
			}),
			invalidatesTags: ['roles', 'users']
		}),
		// Add or update user (no project assignment). When editing (e.g. email changed), pass originalEmail so the backend finds the user to update.
		addUser: builder.mutation({
			query: ({ user, comment = '', originalEmail }) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "users", "user", "upsert"),
				body: {
					instance: { ...user },
					comment,
					...(originalEmail != null && originalEmail !== '' && { originalEmail }),
				},
				method: 'POST',
			}),
			invalidatesTags: ['users']
		}),
		approveUserSalesforce: builder.mutation({
			query: ({ email }) => ({
				url: urljoin('admin', 'users', 'salesforce', encodeURIComponent(email), 'approve'),
				method: 'POST',
			}),
			transformResponse: (response) => ({
				status: response?.status ?? response?.Status ?? '',
			}),
		}),
		// Remove user from project
		removeUserFromProject: builder.mutation({
			query: ({ projectId, email, comment = '' }) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "users", "project", projectId, "remove"),
				body: { instance: { email }, comment },
				method: 'POST',
			}),
			invalidatesTags: ['projects', 'users']
		}),
		// Remove user from role
		removeUserFromRole: builder.mutation({
			query: ({ email, roleName, comment = '' }) => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "users", "user", encodeURIComponent(email), "role", encodeURIComponent(roleName), "remove"),
				body: { instance: { email }, comment },
				method: 'POST',
			}),
			invalidatesTags: ['roles', 'users']
		}),
		// Get projects report
		report: builder.query({
			query: () => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "users", "projects", "report"),
				method: 'GET',
			}),
		}),
		// Template endpoints (kept for backwards compatibility)
		template: builder.query({
			query: (type = '') => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "templates", "get", type),
				method: 'GET',
			}),
		}),
		editTemplate: builder.mutation({
			query: (type = '') => ({
				// baseQueryWithAuth handles authorization header automatically
				url: urljoin("admin", "templates", "set", type),
				method: 'GET',
			}),
		}),
	}),
});

export const {
	useUsersQuery,
	useSearchUsersQuery,
	useRolesQuery,
	useAllRolesQuery,
	useSearchRolesQuery,
	useManagedProjectsQuery,
	useSearchProjectsQuery,
	useUserProjectsQuery,
	useUserRolesQuery,
	useUserLoginStatusQuery,
	useUserTalentLmsSyncQuery,
	useReconcileUserTalentLmsMutation,
	useTalentLmsSyncQueueQuery,
	useTalentLmsSyncFailuresQuery,
	useRunTalentLmsSyncMutation,
	useUserRegistrationEligibilityQuery,
	useProjectUsersQuery,
	useRoleUsersQuery,
	useLazyUserProjectsQuery,
	useLazyUserRolesQuery,
	useLazyUserLoginStatusQuery,
	useLazyUserTalentLmsSyncQuery,
	useLazyProjectUsersQuery,
	useLazyRoleUsersQuery,
	useLazyRolePermissionsQuery,
	useRemoveRolePermissionMutation,
	useAddRolePermissionMutation,
	usePermissionsCatalogQuery,
	useResetRolePermissionsMutation,
	useResetAllRolesMutation,
	useDefaultRolesQuery,
	useAddUserToProjectMutation,
	useAddUsersToProjectMutation,
	useAddProjectsToUserMutation,
	useAddRolesToUserMutation,
	useAddUserToRoleMutation,
	useAddUsersToRoleMutation,
	useAddUserMutation,
	useApproveUserSalesforceMutation,
	useRemoveUserFromProjectMutation,
	useRemoveUserFromRoleMutation,
	useRemoveRolesFromUserMutation,
	useReportQuery,
	useTemplateQuery,
	useEditTemplateMutation,
} = adminApi;

/* 
Available endpoints:
√ GET /api/v1/admin/users - Get all users
√ GET /api/v1/admin/users/search/{text} - Search users
√ GET /api/v1/admin/users/projects - Get all projects
√ GET /api/v1/admin/users/projects/search/{text} - Search projects
√ GET /api/v1/admin/users/roles - Get all roles
√ GET /api/v1/admin/users/roles/all - Get all roles (no pagination)
√ GET /api/v1/admin/users/roles/search/{text} - Search roles
√ POST /api/v1/admin/users/user/upsert - Add or update user
√ POST /api/v1/admin/users/project/{projectId}/add - Add user to project
√ POST /api/v1/admin/users/project/{projectId}/add/collection - Add users to project
√ POST /api/v1/admin/users/user/{email}/projects/add/collection - Add projects to user
√ POST /api/v1/admin/users/user/{email}/roles/add/collection - Add roles to user
√ POST /api/v1/admin/users/user/{email}/role/{roleName}/add - Add user to role
√ POST /api/v1/admin/users/roles/{roleName}/users/add/collection - Add users to role
√ POST /api/v1/admin/users/project/{projectId}/remove - Remove user from project
√ POST /api/v1/admin/users/user/{email}/role/{roleName}/remove - Remove user from role
√ POST /api/v1/admin/users/user/{email}/roles/remove/collection - Remove roles from user
√ GET /api/v1/admin/users/projects/report - Get projects report
 */